import React, { useState, useEffect } from 'react'
import { Button, Icon, Spin } from 'antd'
import { useWardrobe } from '../../../../hooks/useWardrobe'
import { QuickOutfitSelect } from './shared'

/**
 * Self-fetching outfit card for selected/mapped outfits.
 *
 * Instead of relying on the paginated outfit list (which may not contain
 * the selected outfit), this component fetches its own data using the
 * "get" action with the outfit tag.
 *
 * @param {Object} props
 * @param {String} props.outfitKey - The outfit placeholder key (e.g. "__casual__")
 * @param {String} props.tag - The outfit tag currently assigned
 * @param {String} props.description - Human-readable outfit description
 * @param {String} props.initialImage - Optional pre-loaded image URL
 * @param {Array} props.libraryOutfits - Outfits from the library for QuickSelect
 * @param {Function} props.onImageResolved - (outfitKey, imageUrl) => void
 * @param {Function} props.onQuickSelect - (outfitKey, outfit) => void
 * @param {Function} props.onOpenPicker - (outfitKey, mode) => void
 * @param {Function} props.onOpenCreate - (outfitKey) => void
 * @param {String} props.videoId - Video ID for API calls
 */
const SelectedOutfitCard = ({
  outfitKey,
  tag,
  description,
  initialImage,
  libraryOutfits = [],
  onImageResolved,
  onOutfitFetched,
  onQuickSelect,
  onOpenPicker,
  onOpenCreate,
  videoId
}) => {
  const [image, setImage] = useState(initialImage || null)
  const [loading, setLoading] = useState(false)
  const [outfitData, setOutfitData] = useState(null)

  const { getOutfit } = useWardrobe({ videoId })

  const cleanOutfitLabel = outfitKey.replace(/^_{2}|_{2}$/g, '')

  // Fetch outfit data by tag — first check libraryOutfits, then fallback to API
  useEffect(() => {
    let cancelled = false

    const resolveOutfit = async () => {
      if (!tag || image) return

      // Check if outfit already exists in the loaded library
      const existing = libraryOutfits.find(o => (o.tag || o.id) === tag)
      if (existing) {
        const existingImage =
          existing.primary_image ||
          (existing.views && Object.values(existing.views)[0])
        setOutfitData(existing)
        if (existingImage) {
          setImage(existingImage)
          if (onImageResolved) {
            onImageResolved(outfitKey, existingImage)
          }
        }
        return
      }

      // Not in local list — fetch via API
      setLoading(true)
      try {
        const result = await getOutfit(tag)
        if (cancelled) return

        if (result) {
          const resolvedImage =
            result.primary_image ||
            (result.views && Object.values(result.views)[0])

          setOutfitData(result)
          // Push fetched outfit into formData.outfits so picker can find it
          if (onOutfitFetched) {
            onOutfitFetched(result)
          }
          if (resolvedImage) {
            setImage(resolvedImage)
            if (onImageResolved) {
              onImageResolved(outfitKey, resolvedImage)
            }
          }
        }
      } catch (err) {
        console.error(`Failed to fetch outfit for tag "${tag}":`, err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    resolveOutfit()

    return () => {
      cancelled = true
    }
  }, [tag]) // Only re-run when tag changes

  // Sync external image changes (e.g. from picker or create)
  useEffect(() => {
    if (initialImage) {
      setImage(initialImage)
    }
  }, [initialImage])

  return (
    <div
      key={outfitKey}
      className="outfit-item-card clickable"
      onClick={() => onOpenPicker && onOpenPicker(outfitKey, 'view')}
    >
      <div className="outfit-preview-area">
        {loading ? (
          <div
            className="upload-trigger-container empty"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Spin size="small" />
            <span style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              Loading outfit...
            </span>
          </div>
        ) : image ? (
          <div className="image-preview-wrapper">
            <img src={image} alt={outfitKey} />
            <div className="image-overlay">
              <div className="overlay-actions top-actions">
                <Button
                  type="link"
                  icon="eye"
                  className="preview-btn"
                  onClick={e => {
                    e.stopPropagation()
                    if (onOpenPicker) onOpenPicker(outfitKey, 'view')
                  }}
                />
              </div>
              <div className="overlay-actions main-actions">
                <Button
                  type="primary"
                  className="same-width-btn"
                  onClick={e => {
                    e.stopPropagation()
                    if (onOpenCreate) onOpenCreate(outfitKey)
                  }}
                >
                  Create New
                </Button>
                <Button
                  type="default"
                  className="same-width-btn"
                  onClick={e => {
                    e.stopPropagation()
                    if (onOpenPicker) onOpenPicker(outfitKey, 'select')
                  }}
                >
                  Library
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="upload-trigger-container empty">
            <div className="actions-area">
              <div
                className="action-item"
                onClick={e => {
                  e.stopPropagation()
                  if (onOpenCreate) onOpenCreate(outfitKey)
                }}
              >
                <Icon type="plus-circle" />
                <span>Create</span>
              </div>

              <div
                className="action-item"
                onClick={e => {
                  e.stopPropagation()
                  if (onOpenPicker) onOpenPicker(outfitKey, 'select')
                }}
              >
                <Icon type="shop" />
                <span>Library</span>
              </div>
            </div>

            <QuickOutfitSelect
              outfits={libraryOutfits}
              onSelect={outfit =>
                onQuickSelect && onQuickSelect(outfitKey, outfit)
              }
              placeholder="Quick Select..."
              size="default"
            />
          </div>
        )}
      </div>
      <div className="outfit-details">
        <div className="outfit-label">{cleanOutfitLabel}</div>
        {description.includes(':') ? (
          <>
            <div className="outfit-title">
              {description.split(':')[0].trim()}
            </div>
            <div className="outfit-description">
              {description
                .split(':')
                .slice(1)
                .join(':')
                .trim()}
            </div>
          </>
        ) : (
          <div className="outfit-description">{description}</div>
        )}
      </div>
    </div>
  )
}

export default SelectedOutfitCard
